import { Button, Card, CardContent, Input, Label, Logo } from "@exhousing/design-system";
import { KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useAcceptInvite } from "@/hooks/api/useAuth";

/** Journey 1.1: the invited landlord (or staff member) sets a password and is let
 * straight into the portal — no separate login step. */
export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const accept = useAcceptInvite();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Choose a password of at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The passwords do not match.");
      return;
    }
    accept.mutate(
      { token: token ?? "", password },
      {
        onSuccess: () => {
          toast.success("Welcome! Your account is ready.");
          navigate("/dashboard", { replace: true });
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8">
          <div className="mb-6 flex flex-col items-center text-center">
            <Logo className="h-8" />
            <h1 className="mt-5 font-display text-2xl font-medium tracking-tight">
              Set your password
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              You were invited to the Balatin portal. Choose a password to
              activate your account — you're signed in right after.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Repeat password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={accept.isPending}>
              {accept.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <KeyRound className="size-4" />
              )}
              Activate account
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Link expired? Ask your agency to send a new invitation.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
